/**
 * "Policy areas · share of speaking time this term" (profile side card, screenshots/19), Apache ECharts 6.
 * Each area: its name and share on one line, a 6px bar under it. Init once from props that come from the
 * already-hydrated snapshot (no spinner); later updates merge in place by series id.
 */
import { useEffect, useRef } from "react";
import { useThemeVersion } from "../theme/runtime";
import * as echarts from "echarts/core";
import { BarChart } from "echarts/charts";
import { GridComponent, TooltipComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { prefersReducedMotion } from "../motion";

echarts.use([BarChart, GridComponent, TooltipComponent, CanvasRenderer]);
const cssVar = (n: string) => getComputedStyle(document.documentElement).getPropertyValue(n).trim();
const ROW = 50;
const pct = (v: unknown) => `${Math.round(Number(v) * 100)}%`;

export function PolicyAreasChart({ shares }: { shares: { area: string; share: number }[] }) {
  const el = useRef<HTMLDivElement>(null);
  const chart = useRef<echarts.ECharts | null>(null);
  const themeVersion = useThemeVersion();   // re-read the colors when the dashboard theme changes
  const options = (s: typeof shares) => ({
    yAxis: { type: "category", inverse: true, show: false, data: s.map((x) => x.area) },
    series: [
      { id: "track", data: s.map((x) => ({ value: 1, share: x.share })) },
      { id: "share", data: s.map((x) => x.share) },
    ],
  });

  useEffect(() => {
    const c = echarts.init(el.current!, null, { renderer: "canvas" });
    chart.current = c;
    const text = { fontFamily: cssVar("--font"), fontSize: 13 };
    c.setOption({
      animation: !prefersReducedMotion(),
      animationDuration: 300,
      grid: { left: 0, right: 0, top: 22, bottom: 4 },
      xAxis: { type: "value", max: 1, show: false },
      tooltip: { show: false },
      ...options(shares),
      series: [
        { id: "track", type: "bar", barWidth: 6, silent: true, itemStyle: { color: cssVar("--bg-muted"), borderRadius: 3 }, data: shares.map((x) => ({ value: 1, share: x.share })),
          label: { show: true, position: ["100%", -19], align: "right", ...text, fontSize: 12, color: cssVar("--text-secondary"),
            formatter: (p: { data: { share: number } }) => pct(p.data.share) } },
        { id: "share", type: "bar", barWidth: 6, barGap: "-100%", itemStyle: { color: cssVar("--link"), borderRadius: 3 }, data: shares.map((x) => x.share),
          label: { show: true, position: [0, -19], ...text, color: cssVar("--text-primary"), formatter: (p: { name: string }) => p.name } },
      ],
    });
    const ro = new ResizeObserver(() => c.resize());
    ro.observe(el.current!);
    return () => { ro.disconnect(); c.dispose(); chart.current = null; };
  }, [themeVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { chart.current?.setOption(options(shares), { lazyUpdate: true }); }, [shares]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div ref={el} style={{ height: shares.length * ROW }} role="img"
      aria-label={`Policy areas by share of speaking time: ${shares.map((s) => `${s.area} ${pct(s.share)}`).join(", ")}`} />
  );
}
